import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import MapView from "@/components/MapView";
import ElevationProfile from "@/components/ElevationProfile";
import BremaLogo from "@/components/BremaLogo";
import { Bike, Timer, Route as RouteIcon, ArrowRight, Download, Sparkles } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useRouteWeather } from "@/hooks/useRouteWeather";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicRoute() {
  const { t } = useI18n();
  const { shareId } = useParams();
  const [route, setRoute] = useState(null);
  const [error, setError] = useState(null);
  const [gpxLoading, setGpxLoading] = useState(false);
  const { weatherPoints } = useRouteWeather(route?.polyline, { enabled: !!route?.polyline });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${BACKEND}/api/routes/public/${shareId}`
        );
        if (!cancelled) setRoute(data);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.status === 404
              ? t("publicNotFound")
              : t("publicLoadError")
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shareId, t]);

  const downloadGpx = async () => {
    if (!route?.polyline?.length) return;
    setGpxLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND}/api/routes/export/gpx`,
        {
          name: `ValliRider ${route.distance_km}km ${route.vehicle}`,
          vehicle: route.vehicle,
          polyline: route.polyline,
        },
        { responseType: "blob" }
      );
      const filename = `vallirider-${route.distance_km}km-${route.vehicle}.gpx`;
      const blob = new Blob([res.data], { type: "application/gpx+xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // silent — the CTA remains available
    } finally {
      setGpxLoading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center">
        <BremaLogo height={60} />
        <div className="font-display font-bold uppercase text-brema-red text-xl">
          {error}
        </div>
        <Link
          to="/"
          className="font-mono uppercase tracking-widest text-brema-yellow border border-brema-yellow px-4 py-2 text-xs hover:bg-brema-yellow hover:text-brema-obsidian transition"
        >
          {t("publicBackHome")}
        </Link>
      </div>
    );
  }

  if (!route) {
    return (
      <div className="min-h-screen flex items-center justify-center text-brema-yellow font-mono text-xs uppercase tracking-widest">
        {t("publicLoading")}
      </div>
    );
  }

  const start = {
    name: route.start_label,
    lat: route.start_lat,
    lng: route.start_lng,
  };
  const durHours = Math.floor(route.duration_min / 60);
  const durMins = route.duration_min % 60;
  const routeTypeLabel =
    route.route_type === "destination"
      ? t("publicDestType")
      : route.route_type === "andata-ritorno"
      ? t("publicARType")
      : t("publicLoopType");

  return (
    <div className="min-h-screen text-white">
      <header className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between border-b border-white/10">
        <Link to="/" className="flex items-center gap-3">
          <BremaLogo height={46} />
          <div className="border-l border-white/15 pl-3 leading-none">
            <div className="font-display font-black uppercase text-lg tracking-[0.2em] text-white">
              ValliRider
            </div>
            <div className="font-mono text-[9px] text-brema-yellow uppercase tracking-widest mt-1">
              {t("publicSharedRoute")}
            </div>
          </div>
        </Link>
        <Link
          to="/"
          data-testid="public-cta-create"
          className="hidden sm:inline-flex items-center gap-2 font-display font-bold uppercase tracking-widest text-xs bg-brema-red text-white px-4 py-2 hover:bg-[#D90429] transition"
        >
          {t("publicCreateYours")} <ArrowRight size={14} />
        </Link>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-4">
        <div>
          <div
            className="font-mono text-[10px] text-brema-yellow uppercase tracking-widest"
            data-testid="public-route-vehicle"
          >
            {route.vehicle === "moto" ? "Moto" : "Bike / E-Bike"} · {routeTypeLabel}
          </div>
          <h1
            className="mt-1 font-display font-black uppercase text-2xl sm:text-4xl tracking-tight"
            data-testid="public-route-name"
          >
            {route.name}
          </h1>
          <div className="mt-1 font-mono text-xs text-zinc-300">
            {route.start_label} · {start.lat.toFixed(4)}°N · {start.lng.toFixed(4)}°E
          </div>
        </div>

        <div className="relative bg-brema-coal border border-white/10 h-[520px] overflow-hidden">
          <span className="corner-tick tl" />
          <span className="corner-tick tr" />
          <span className="corner-tick bl" />
          <span className="corner-tick br" />
          <MapView start={start} polyline={route.polyline} weatherPoints={weatherPoints} />
        </div>

        <div className="grid grid-cols-3 gap-2">
          <StatBox
            testId="public-stat-km"
            icon={<RouteIcon size={13} />}
            label={t("distance")}
            value={route.distance_km}
            unit="KM"
          />
          <StatBox
            testId="public-stat-duration"
            icon={<Timer size={13} />}
            label={t("duration")}
            value={`${durHours}h ${durMins}m`}
            unit=""
          />
          <StatBox
            testId="public-stat-difficulty"
            icon={<Bike size={13} />}
            label={t("difficulty")}
            value={route.difficulty}
            unit=""
            highlight
          />
        </div>

        {route.elevation_profile && route.elevation_profile.length > 1 && (
          <ElevationProfile
            profile={route.elevation_profile}
            min_elev_m={route.min_elev_m}
            max_elev_m={route.max_elev_m}
            elevation_gain_m={route.elevation_gain_m}
            elevation_loss_m={route.elevation_loss_m}
          />
        )}

        {/* Primary CTA: free GPX download for any visitor — no signup required */}
        <div className="mt-6 border border-brema-yellow/40 bg-brema-yellow/5 p-5">
          <button
            onClick={downloadGpx}
            disabled={gpxLoading}
            data-testid="public-download-gpx"
            className="w-full bg-brema-yellow hover:bg-amber-400 disabled:opacity-60 text-brema-obsidian font-display font-black uppercase tracking-widest px-4 py-4 flex items-center justify-center gap-3 transition-colors text-sm sm:text-base"
          >
            <Download size={18} />
            {gpxLoading ? t("publicGpxLoading") : t("publicGpxCta")}
          </button>
          <div className="mt-3 font-mono text-[10px] text-zinc-300 uppercase tracking-widest text-center">
            {t("publicGpxHint")}
          </div>
        </div>

        {/* Conversion banner */}
        <div
          data-testid="public-conversion-banner"
          className="mt-3 relative overflow-hidden border border-brema-red/60 bg-gradient-to-r from-brema-red/10 to-transparent p-5"
        >
          <span className="corner-tick tl" />
          <span className="corner-tick br" />
          <div className="flex items-start gap-3">
            <Sparkles className="text-brema-red flex-shrink-0 mt-0.5" size={18} />
            <div className="flex-1">
              <div className="font-display font-black uppercase text-white text-base sm:text-lg leading-tight">
                {t("publicLikeQ")}
              </div>
              <div className="mt-1 text-sm text-zinc-200 leading-relaxed">
                {t("publicConvertDesc")}
              </div>
              <Link
                to="/"
                data-testid="public-cta-signup"
                className="mt-3 inline-flex items-center gap-2 bg-brema-red hover:bg-[#D90429] text-white font-display font-bold uppercase tracking-widest text-xs px-4 py-2.5 transition-colors"
              >
                {t("publicOpenApp")} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatBox({ icon, label, value, unit, highlight, testId }) {
  return (
    <div
      data-testid={testId}
      className={`bg-brema-obsidian/60 border px-3 py-3 ${
        highlight ? "border-brema-red/60" : "border-white/10"
      }`}
    >
      <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-zinc-400">
        <span className="text-zinc-300">{icon}</span>
        {label}
      </div>
      <div className="mt-1 font-display font-bold text-xl leading-none text-white">
        {value}
        {unit && (
          <span className="ml-1 text-[10px] font-mono text-zinc-400">
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}
